import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import CookieConsent from '@/components/CookieConsent';
import NewsletterPopup from '@/components/NewsletterPopup';
import CompetitionTicker from '@/components/CompetitionTicker';
import CompetitionPromo from '@/components/CompetitionPromo';
import B2BTicker from '@/components/b2b/B2BTicker';
import LiveFeedTicker from '@/components/telemetry/LiveFeedTicker';
import { BrandAssetsProvider } from '@/lib/BrandAssetsContext';
import { ChatProvider } from '@/lib/ChatContext';
import GlobalChatbot from '@/components/support/GlobalChatbot';
import Navbar from './Navbar';
import Footer from './Footer';

export default function AppLayout() {
  const { pathname } = useLocation();
  // B2B trade portal is a focused, logged-in flow — suppress marketing pop-ups there.
  const suppressPopups = pathname.startsWith('/b2b');
  // Live telemetry page gets the green broadcast-status bar instead of the competition ad.
  const isLivePage = pathname.startsWith('/live');

  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const [tickerOn, setTickerOn] = useState(() => {
    if (suppressPopups) return false;
    try { return !sessionStorage.getItem('mr_comp_ticker_dismissed'); } catch { return true; }
  });

  const closeTicker = () => {
    setTickerOn(false);
    try { sessionStorage.setItem('mr_comp_ticker_dismissed', '1'); } catch {}
  };

  // Single source of truth for every sticky sub-header. The navbar sits at
  // top-9 (36px) whenever a ticker is shown, so the combined header height is
  // 36px ticker + 64px navbar = 100px; otherwise just the 64px navbar.
  const headerOffset = isFullscreen ? '0px' : (tickerOn || suppressPopups || isLivePage ? '96px' : '64px');

  // Set the offset on the document root AFTER mount (never during render) so
  // the render path never touches a browser global. The :root default in
  // index.css covers first paint; this updates it when the ticker toggles.
  useEffect(() => {
    document.documentElement.style.setProperty('--header-offset', headerOffset);
  }, [headerOffset]);

  return (
    <BrandAssetsProvider>
      <ChatProvider>
        <div className="min-h-screen bg-[#0d0d0f] text-white">
          {!isFullscreen && (suppressPopups ? <B2BTicker /> : isLivePage ? <LiveFeedTicker /> : (tickerOn && <CompetitionTicker onClose={closeTicker} />))}
          {!isFullscreen && <Navbar topOffset={tickerOn || suppressPopups || isLivePage} />}
          <main className={!isFullscreen && (tickerOn || suppressPopups || isLivePage) ? 'pt-8' : ''}>
            <Outlet />
          </main>
          <Footer />
          {!suppressPopups && <NewsletterPopup />}
          {!suppressPopups && <CompetitionPromo />}
          <CookieConsent />
          <GlobalChatbot />
        </div>
      </ChatProvider>
    </BrandAssetsProvider>
  );
}